import { Badge, Box, Button, Collapse, HStack, Icon, Stack, Text, useDisclosure } from "@chakra-ui/react";
import type { KeyboardEvent, MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { FiMessageSquare } from "react-icons/fi";
import type { CommentSubjectKind } from "../../../../../../autogen/bff-api";
import { TipTap, isTiptapContentEmpty } from "../../../../../../common/input/TipTap/TipTap";
import { BusinessSecretNote } from "../../../../../market-dialogues/BusinessSecretNote";
import { useItemComments } from "./CommentRoundContext";

const stayInRow = (e: MouseEvent | KeyboardEvent) => e.stopPropagation();

interface Props {
  kind: CommentSubjectKind;
  itemRef: string | null | undefined;
  defaultOpen?: boolean;
}

export const ItemComments = ({ kind, itemRef, defaultOpen = false }: Props) => {
  const { t } = useTranslation();
  const comments = useItemComments(kind, itemRef);
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: defaultOpen });
  if (comments.length === 0) return null;

  return (
    <Box mt="2" onClick={stayInRow} onKeyDown={stayInRow} data-testid="item-comments">
      <Button
        size="xs"
        variant="ghost"
        colorScheme="teal"
        leftIcon={<Icon as={FiMessageSquare} />}
        aria-expanded={isOpen}
        onClick={onToggle}
      >
        {t("{{count}} comments from the comment round", { count: comments.length })}
      </Button>
      <Collapse in={isOpen} animateOpacity>
        <Stack spacing="2" mt="2">
          {comments.map((comment, index) => (
            <Box
              key={`${comment.dialogueId}-${comment.supplierName}-${index}`}
              borderLeftWidth="3px"
              borderColor="smBorder"
              pl="3"
              py="1"
            >
              <HStack spacing="2" wrap="wrap" mb="1">
                <Text fontSize="sm" fontWeight="semibold">
                  {comment.supplierName}
                </Text>
                <Badge variant="subtle">{t("Round {{number}}", { number: comment.roundNumber })}</Badge>
              </HStack>
              {comment.choice !== undefined && comment.choice !== null && (
                <Text fontSize="sm" fontWeight="semibold">
                  {comment.choice ? t("Yes") : t("No")}
                </Text>
              )}
              {!isTiptapContentEmpty(comment.text) && (
                <TipTap content={comment.text} editable={false} hasBorder={false} padding="0" maxHeight="40" />
              )}
              {(comment.documents ?? []).length > 0 && (
                <Text fontSize="xs" color="smMuted" mt="1">
                  {t("Attached: {{names}} – open the market dialogue to read them", {
                    names: (comment.documents ?? []).map((document) => document.name).join(", "),
                  })}
                </Text>
              )}
              {comment.withheld && (
                <BusinessSecretNote
                  label={t("Part of this answer is marked as a business secret")}
                  reason={t("Only the market dialogue team can read it.")}
                />
              )}
              {comment.secretReason && (
                <BusinessSecretNote label={t("The answer is marked as a business secret")} reason={comment.secretReason} />
              )}
              {(comment.documents ?? [])
                .filter((document) => document.secretReason)
                .map((document) => (
                  <BusinessSecretNote
                    key={document.name}
                    label={t("«{{name}}» is marked as a business secret", { name: document.name })}
                    reason={document.secretReason ?? ""}
                  />
                ))}
            </Box>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
};
